export default function OfficePartyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="relative h-96 bg-gradient-to-br from-blue-600 to-teal-600 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=1200&h=600&fit=crop"
          alt="Office celebration"
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-40"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
          <h1 className="text-5xl font-bold text-white mb-4">Office Party Gift Exchange</h1>
          <p className="text-xl text-white max-w-2xl">
            Build team camaraderie and celebrate success with a fun workplace gift exchange
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Workplace Gift Exchanges Made Simple</h2>
          <p className="text-lg text-gray-600 mb-6">
            Office gift exchanges are a proven way to boost morale, strengthen team bonds, and create lasting memories.
            SwapGifts handles the logistics so you can focus on celebrating with your colleagues.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="border border-gray-200 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-2">Perfect for</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• End-of-year holiday parties</li>
                <li>• Team building celebrations</li>
                <li>• Department-specific exchanges</li>
                <li>• Company-wide events</li>
                <li>• Remote and hybrid teams</li>
              </ul>
            </div>

            <div className="border border-gray-200 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-2">Typical budget range</h3>
              <p className="text-gray-600 text-sm mb-3">
                Office exchanges typically range from $15 to $50. Keep it modest to ensure everyone can participate
                comfortably regardless of position or salary.
              </p>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4">Planning Your Office Exchange</h3>
          <div className="space-y-4 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                1
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Get management approval</h4>
                <p className="text-gray-600 text-sm">
                  Coordinate with HR or leadership to ensure the event aligns with company culture and policies.
                  Confirm timing, budget guidelines, and whether participation is optional.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                2
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Build your participant list</h4>
                <p className="text-gray-600 text-sm">
                  Include everyone who wants to participate. For large companies, consider department-level exchanges.
                  For remote teams, plan ahead for shipping logistics.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                3
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Set professional guidelines</h4>
                <p className="text-gray-600 text-sm">
                  Establish clear rules: appropriate gifts only, no inside jokes that exclude others, and respect
                  dietary restrictions or personal preferences shared in wishlists.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                4
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Execute and celebrate</h4>
                <p className="text-gray-600 text-sm">
                  Draw names 2-3 weeks before your party. Schedule a dedicated time during your event for the gift reveal.
                  Consider adding fun elements like white elephant rules or themed categories.
                </p>
              </div>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4">Professional Exchange Best Practices</h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Keep budgets modest and enforce them strictly to avoid awkwardness</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Encourage neutral, crowd-pleasing gifts suitable for any colleague</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Use exclusions to prevent manager-direct report pairings if that feels awkward</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Account for remote workers with digital gift options or shipped items</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Make participation optional but encouraged to maintain team spirit</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Consider themed exchanges like "desk items" or "self-care" for variety</span>
              </li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <h4 className="font-semibold text-yellow-900 mb-2">Important Considerations</h4>
            <p className="text-yellow-800 text-sm mb-3">
              Office exchanges require extra sensitivity to maintain professionalism and inclusivity:
            </p>
            <ul className="space-y-2 text-yellow-800 text-sm">
              <li>• Avoid alcohol or controversial items unless you know your workplace culture well</li>
              <li>• Respect religious and cultural differences in your group</li>
              <li>• Keep gifts appropriate for all ages and backgrounds</li>
              <li>• Don't pressure anyone to participate beyond their comfort level</li>
            </ul>
          </div>

          <div className="text-center">
            <a
              href="/create"
              className="inline-block px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              Start Your Office Exchange
            </a>
            <p className="text-sm text-gray-500 mt-3">No registration required • Free to use</p>
          </div>
        </div>

        <div className="bg-gray-100 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-3">Related Resources</h3>
          <div className="flex flex-wrap gap-3">
            <a href="/events/christmas" className="text-sm text-gray-700 hover:text-blue-600">
              Christmas Exchanges →
            </a>
            <a href="/blog" className="text-sm text-gray-700 hover:text-blue-600">
              Gift Ideas Blog →
            </a>
            <a href="/faq" className="text-sm text-gray-700 hover:text-blue-600">
              FAQ →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
